import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { CircleNotch, Warning } from '@phosphor-icons/react';
import { getToken } from '@/lib/api';
import { DocumentTextOverlay } from './DocumentTextOverlay';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const A4 = 210 / 297;

// Eigen PDF-weergave (pdf.js): elke pagina volledig zichtbaar, gestapeld, geschaald op het paneel.
// fit='width' → pagina zo breed als het paneel; fit='page' → hele pagina past in de hoogte.
export const PdfViewer = ({ url, zoom = 100, fit = 'width', onPages, docId, activeField, onSelect, selected }) => {
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [numPages, setNumPages] = useState(0);
  const [ratio, setRatio] = useState(A4);
  const [fout, setFout] = useState('');

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const meet = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pad = 16;
  const basis = fit === 'page'
    ? Math.min(box.w - pad, Math.max(120, (box.h - pad - 22) * ratio))
    : box.w - pad;
  const breedte = Math.max(120, Math.round(basis * (zoom / 100)));
  // `file` moet referentieel stabiel zijn: een nieuw object per render laat react-pdf het document
  // vernietigen en herladen terwijl pagina's nog renderen (→ 'sendWithPromise' op null).
  const token = getToken();
  const file = useMemo(() => ({ url, httpHeaders: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }), [url, token]);

  return (
    <div ref={boxRef} className="w-full h-full overflow-auto bg-slate-200" data-testid="pdf-viewer">
      {box.w > 0 && (
        <Document
          file={file}
          loading={<div className="p-6 text-center text-xs text-slate-500 inline-flex items-center gap-2 w-full justify-center"><CircleNotch size={16} className="animate-spin" /> PDF laden…</div>}
          error={<div className="p-6 text-center text-xs text-red-700 inline-flex items-center gap-2 w-full justify-center"><Warning size={16} weight="fill" /> {fout || 'PDF kon niet geladen worden.'} <a href={url} target="_blank" rel="noopener noreferrer" className="underline">Openen in nieuw venster</a></div>}
          onLoadSuccess={(pdf) => { setNumPages(pdf.numPages); onPages?.(pdf.numPages); }}
          onLoadError={(e) => setFout(e?.message || '')}
        >
          <div className="flex flex-col items-center gap-3 py-2">
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="flex flex-col items-center" data-testid={`pdf-page-${i + 1}`}>
                <div className="relative">
                <Page
                  pageNumber={i + 1}
                  width={breedte}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-[0_2px_14px_rgba(0,0,0,0.18)] bg-white"
                  onLoadSuccess={(p) => { if (i === 0 && p.width && p.height) setRatio(p.width / p.height); }}
                />
                {docId && <DocumentTextOverlay docId={docId} pageNumber={i+1} active={activeField} onSelect={onSelect} selected={selected} />}
                </div>
                <div className="mt-1 text-[10px] font-mono text-slate-500">Pagina {i + 1} / {numPages}</div>
              </div>
            ))}
          </div>
        </Document>
      )}
    </div>
  );
};

export default PdfViewer;
